import type { ComponentProps } from 'react';
import { Hide, mq, StyleSheet } from 'react-native-unistyles';
import Text from '@/components/UI/Text';
import Box from '../UI/Box';
import Pressable from '../Unistyles/Pressable';

export const Button = (props: ComponentProps<typeof Pressable>) => {
  return <Pressable {...props} style={[style.buttonStyle, props.style]} />;
};

export const ButtonGroup = (props: ComponentProps<typeof Box>) => {
  return <Box direction="horizontal" space="xs" {...props} />;
};

export const ColorBar = ({
  backgroundColor,
  ...rest
}: ComponentProps<typeof Box>) => {
  const resolvedBackgroundColor =
    typeof backgroundColor === 'string' && backgroundColor.trim().length > 0
      ? backgroundColor
      : 'transparent';

  return (
    <Box
      height={4}
      w="full"
      {...rest}
      backgroundColor={resolvedBackgroundColor}
    />
  );
};

export const Main = (props: ComponentProps<typeof Box>) => {
  return (
    <Box
      direction="horizontal"
      space="md"
      alignItems="center"
      style={style.main}
      {...props}
    />
  );
};

export const Start = ({ children }: ComponentProps<typeof Box>) => {
  return (
    <Hide mq={mq.only.width('md')}>
      <Box
        direction="horizontal"
        justifyContent="flex-start"
        alignItems="center"
        space="md"
        style={style.start}
      >
        {children}
      </Box>
    </Hide>
  );
};

export const End = ({ children }: ComponentProps<typeof Box>) => {
  return (
    <Box
      direction="horizontal"
      justifyContent="flex-end"
      alignItems="center"
      space="md"
      style={style.end}
    >
      {children}
    </Box>
  );
};

export const Row = (props: ComponentProps<typeof Box>) => {
  return (
    <Box direction="horizontal" alignItems="center" space="md" {...props} />
  );
};

export const TitleContainer = (props: ComponentProps<typeof Box>) => {
  return <Box style={style.titleContainer} {...props} />;
};

export const MastheadTitleContainer = (props: ComponentProps<typeof Box>) => {
  return <Box {...props} style={[style.mastheadTitleContainer, props.style]} />;
};

export const Title = (props: ComponentProps<typeof Text>) => {
  return <Text size="3xl" numberOfLines={1} {...props} />;
};

export const Toolbar = (props: ComponentProps<typeof Box>) => {
  return <Box space="xs" px="xl" pt="xl" pb="md" {...props} />;
};

const style = StyleSheet.create((theme, rt) => ({
  buttonGroup: {
    paddingHorizontal: theme.space(3),
    _web: {
      paddingRight: theme.space(3),
    },
  },
  start: {
    _web: {
      paddingLeft: theme.space(3),
    },
  },
  end: {
    paddingLeft: theme.space(3),
    _web: {
      paddingRight: theme.space(3),
    },
  },
  main: {
    justifyContent: 'space-between',
    paddingHorizontal: theme.space(3),
    _web: {
      paddingTop: rt.insets.top + theme.space(3),
      paddingBottom: theme.space(3),
    },
  },
  buttonStyle: {
    width: theme.size(10),
    height: theme.size(10),
    borderRadius: theme.borderRadius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mastheadTitleContainer: {
    paddingHorizontal: {
      base: 0,
      md: theme.space(3),
    },
    flexDirection: 'row',
    gap: theme.space('md'),
    flexShrink: 1,
    alignItems: 'center',
    minWidth: 0,
  },
  titleContainer: {
    paddingHorizontal: {
      base: 0,
      md: theme.space(3),
    },
    flex: 1,
    flexDirection: 'row',
    _web: {
      alignItems: 'center',
    },
    gap: theme.space('md'),
  },
}));
